import React, { useState, useRef, useEffect } from "react";
import styles from './SearchableDropdown.module.css';
import { Checkbox, FormControlLabel } from "@mui/material";

// Define the type for the options
interface Option {
  label: string;
  id: any;
}

interface SearchableDropdownProps {
  options: Option[];
  selectedValues?: any[];
  onSelect: (values: any[]) => any;
  multiSelect?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  selectedValues = [],
  onSelect,
  multiSelect = false,
}) => {
  // State for managing dropdown visibility, search input, and highlighted index
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [localSelectedValues, setLocalSelectedValues] = useState<any[]>(selectedValues);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLocalSelectedValues(selectedValues);
  }, [selectedValues]);

  // Filtered options based on search query, ensuring label is a valid string
  const filteredOptions = options.filter((option:any) =>
    option.label && option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (option: Option) => {
    let newSelectedValues: any[];
    
    if (multiSelect) {
      // Toggle selection for multi-select
      newSelectedValues = localSelectedValues.includes(option.id)
        ? localSelectedValues.filter(id => id !== option.id)
        : [...localSelectedValues, option.id];
    } else {
      // Single select behavior
      newSelectedValues = [option.id];
      setIsOpen(false);
    }

    setLocalSelectedValues(newSelectedValues);
    onSelect(newSelectedValues);
  };

  const handleSelectAll = () => {
    if (!multiSelect) return;

    const allSelected = localSelectedValues.length === options.length;
    const newSelectedValues = allSelected ? [] : options.map(option => option.id);
    
    setLocalSelectedValues(newSelectedValues);
    onSelect(newSelectedValues);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleHover = (index: number) => {
    setHoveredIndex(index);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // Delay closing the dropdown to allow click events to trigger
    setTimeout(() => {
      if (!dropdownRef.current?.contains(event.relatedTarget as Node)) {
        setIsOpen(false);
      }
    }, 0);
  };

  const getSelectedLabel = () => {
    if (!multiSelect) {
      // Add debugging to help identify the issue
      const selectedOption = options.find((opt) => String(opt.id) === String(selectedValues[0]));
      return selectedValues[0] && selectedOption
        ? selectedOption.label 
        : "Select";
    }
    
    const selectedLabels = options
      .filter(opt => localSelectedValues.includes(opt.id) || localSelectedValues.includes(String(opt.id)))
      .map(opt => opt.label);
    
    return selectedLabels.length > 0 
      ? `${selectedLabels.length} selected` 
      : "Select";
  };

  return (
    <div ref={dropdownRef} className={styles['dropdown-container']} onBlur={handleBlur} tabIndex={0}>
      <div
        className={styles['dropdown-selected']}
        onClick={() => {setIsOpen((prev) => !prev); setSearchQuery("");}}
      >
        <span className={styles['dropdown-selected-text']}>{getSelectedLabel()}</span>
        <span className={styles['dropdown-arrow']}>{isOpen ? "▲" : "▼"}</span>
      </div>

      {isOpen && (
        <div className={styles['dropdown-list']}>
          <input
            type="text"
            className={styles['dropdown-search']}
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {multiSelect && (
            <div className={styles['select-all']}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localSelectedValues.length === options.length}
                    indeterminate={localSelectedValues.length > 0 && localSelectedValues.length < options.length}
                    onChange={handleSelectAll}
                  />
                }
                label="Select All"
              />
            </div>
          )}
          <ul>
            {filteredOptions.map((option:any, index:any) => (
              <li
                key={option.id}
                className={`${styles['dropdown-item']} ${hoveredIndex === index ? styles['hovered'] : ''}`}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => handleHover(index)}
              >
                {multiSelect ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localSelectedValues.includes(option.id)}
                        onChange={() => handleSelect(option)}
                      />
                    }
                    label={option.label}
                  />
                ) : (
                  option.label
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
